import { useEffect, useState } from 'react';
import { env } from '@/config/env';
import { getSupabase } from '@/lib/supabaseClient';
import { invalidate } from './useAsync';

export type LiveStatus = 'off' | 'connecting' | 'live' | 'error';

/** Which cached screens to refresh when a table changes. */
const TABLE_TAGS: Record<string, string[]> = {
  tenders: ['tenders', 'dashboard', 'vendors', 'lookups'],
  quantity_transactions: ['quantity', 'lineItems', 'tenders', 'dashboard', 'alerts'],
  tender_line_items: ['lineItems', 'quantity', 'tenders', 'dashboard'],
  financial_transactions: ['financials', 'tenders', 'dashboard'],
  documents: ['documents', 'tenders', 'dashboard'],
  amendments: ['amendments', 'tenders', 'dashboard'],
  vendors: ['vendors', 'lookups'],
  departments: ['lookups'],
  locations: ['lookups'],
  notifications: ['notifications'],
  profiles: ['users', 'tenders'],
  tender_alert_events: ['alerts'],
  alert_deliveries: ['alerts'],
  app_settings: ['settings', 'tenders', 'dashboard'],
  audit_logs: ['audit'],
};

/**
 * Subscribes to Supabase Realtime for all business tables. When anyone changes data,
 * the affected screens reload automatically (batched, so a burst of changes = one refresh).
 */
export function useRealtimeSync(enabled: boolean): LiveStatus {
  const [status, setStatus] = useState<LiveStatus>(env.isSupabaseConfigured && enabled ? 'connecting' : 'off');

  useEffect(() => {
    if (!env.isSupabaseConfigured || !enabled) {
      setStatus('off');
      return;
    }
    const sb = getSupabase();
    const pending = new Set<string>();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const flush = () => {
      timer = null;
      const tags = [...pending];
      pending.clear();
      invalidate(...tags);
    };

    let channel = sb.channel('gem-tcms-live');
    Object.entries(TABLE_TAGS).forEach(([table, tags]) => {
      channel = channel.on('postgres_changes', { event: '*', schema: 'public', table }, () => {
        tags.forEach((t) => pending.add(t));
        if (!timer) timer = setTimeout(flush, 600);
      });
    });
    channel.subscribe((s: string) => {
      if (s === 'SUBSCRIBED') setStatus('live');
      else if (s === 'CHANNEL_ERROR' || s === 'TIMED_OUT') setStatus('error');
      else if (s === 'CLOSED') setStatus('off');
    });

    return () => {
      if (timer) clearTimeout(timer);
      void sb.removeChannel(channel);
    };
  }, [enabled]);

  return status;
}
